// One API client for the app. Three modules already carried comments asking
// for this (`screens/ops/api.ts`, `screens/analytics/api.ts`,
// `screens/overview/api.ts`: "polish should hoist one shared client rather
// than let a third copy appear"). The entitlement work made it necessary
// rather than tidy: five modules — review, equations review, corpus config,
// merges, sources — sent NO credential at all, and their routes are now
// owner-gated, so each would otherwise have grown its own sixth copy of
// `authHeaders`.
//
// TWO CREDENTIALS, AND THEY ARE NOT INTERCHANGEABLE:
//
//   * the session cookie — the HUMAN credential, for a signed-in person in
//     a browser. It rides along automatically under `same-origin`.
//   * `X-mAiSight-Key`  — the MACHINE credential, for the embed widget and
//     server-side callers.
//
// The backend's `resolve_org_slug` accepts either, tries the cookie first,
// and never merges them. Sending both is fine and is what `just dev` does.

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

const KEY_STORAGE = "maisight_key";

/** The license key, if this browser has one. See `dashboard/api.ts` for why
 *  localStorage and not the URL. */
export function getKey(): string | null {
  try {
    const stored = window.localStorage.getItem(KEY_STORAGE);
    if (stored) return stored;
  } catch {
    return null; // Safari private mode throws on localStorage access.
  }
  // `just dev` mints a license and passes it in so the app opens already
  // authenticated. The DEV guard is load-bearing: Vite replaces it with
  // `false` in a production build and the branch is stripped, so no key can
  // be baked into a shipped bundle even if the var is set.
  return import.meta.env.DEV
    ? ((import.meta.env?.VITE_MAISIGHT_KEY as string | undefined) ?? null)
    : null;
}

/** Absolute URL for `path`. API_BASE is relative ("/api") in deployed builds
 *  and absolute in dev, and `URL` needs an explicit base for the former. */
export function apiUrl(path: string): string {
  return new URL(
    `${API_BASE}${path}`,
    typeof window !== "undefined" ? window.location.origin : undefined,
  ).toString();
}

/** `fetch` with both credentials attached and JSON defaults.
 *
 *  `credentials: "same-origin"`, NOT `"include"`, and the difference is not
 *  cosmetic: the API sets `allow_credentials=False`, so a cross-origin fetch
 *  carrying `include` is rejected by the browser before the response is read
 *  and every panel goes dark. Same reasoning as `screens/commerce/api.ts`.
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const key = getKey();
  return fetch(apiUrl(path), {
    ...init,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(key ? { "X-mAiSight-Key": key } : {}),
      ...(init.headers ?? {}),
    },
  });
}

/** `apiFetch` + status check + JSON parse. `label` names the operation in the
 *  thrown message so a failure is attributable without a stack trace. */
export async function apiJson<T>(
  label: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) throw new Error(`${label} failed: HTTP ${res.status}`);
  return (await res.json()) as T;
}
